import React from 'react';
import { Truck, ArrowRight, CreditCard, MapPin } from 'lucide-react';

const CheckoutPage = ({ cartItems, setCurrentPage }) => {
  const shippingFee = 10;
  const freeShippingThreshold = 200;

  const calculateSubtotal = () => {
    return cartItems.reduce((total, item) => total + item.price * item.quantity, 0);
  };

  const subtotal = calculateSubtotal();
  const shippingCost = subtotal > freeShippingThreshold ? 0 : shippingFee;
  const total = subtotal + shippingCost;

  const handleConfirmOrder = (event) => {
    event.preventDefault();
    alert('تم تأكيد طلبك بنجاح! شكراً لك.');
    setCurrentPage('home');
  };

  const inputClass =
    'w-full rounded-xl border-2 border-gray-200 px-4 py-2.5 outline-none transition-colors duration-200 focus:border-[#10B981]';
  const labelClass = 'mb-1.5 block text-sm font-semibold text-gray-700';

  return (
    <section className="bg-gray-50 py-16 font-['Cairo'] lg:py-24">
      <div className="container mx-auto px-4">
        <div className="mb-12 text-center">
          <div className="mb-4 flex items-center justify-center gap-2">
            <Truck className="h-6 w-6 text-[#10B981]" />
            <span className="font-semibold text-[#10B981]">إتمام الشراء</span>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 lg:text-4xl">أنهِ طلبك</h2>
        </div>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          {/* قسم معلومات الشحن والدفع */}
          <div className="space-y-6 lg:col-span-2">
            <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-100">
              <h3 className="mb-6 flex items-center gap-2 text-xl font-bold text-gray-900">
                <MapPin className="h-5 w-5 text-[#10B981]" />
                معلومات الشحن
              </h3>
              <form className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="col-span-1">
                  <label htmlFor="fullName" className={labelClass}>الاسم الكامل</label>
                  <input type="text" id="fullName" className={inputClass} placeholder="الاسم الكامل" />
                </div>
                <div className="col-span-1">
                  <label htmlFor="phoneNumber" className={labelClass}>رقم الهاتف</label>
                  <input type="tel" id="phoneNumber" className={inputClass} placeholder="رقم الهاتف" />
                </div>
                <div className="md:col-span-2">
                  <label htmlFor="address" className={labelClass}>العنوان</label>
                  <input type="text" id="address" className={inputClass} placeholder="الشارع، الحي، المدينة" />
                </div>
              </form>
            </div>

            {/* قسم معلومات الدفع */}
            <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-100">
              <h3 className="mb-6 flex items-center gap-2 text-xl font-bold text-gray-900">
                <CreditCard className="h-5 w-5 text-[#10B981]" />
                معلومات الدفع
              </h3>
              <form className="grid grid-cols-1 gap-4">
                <div className="col-span-1">
                  <label htmlFor="cardNumber" className={labelClass}>رقم البطاقة</label>
                  <input type="text" id="cardNumber" className={inputClass} placeholder="xxxx-xxxx-xxxx-xxxx" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="expiryDate" className={labelClass}>تاريخ الانتهاء</label>
                    <input type="text" id="expiryDate" className={inputClass} placeholder="MM/YY" />
                  </div>
                  <div>
                    <label htmlFor="cvv" className={labelClass}>رمز الأمان (CVV)</label>
                    <input type="text" id="cvv" className={inputClass} placeholder="xxx" />
                  </div>
                </div>
              </form>
            </div>
          </div>

          {/* ملخص الطلب */}
          <div className="h-fit rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-100 lg:col-span-1">
            <h3 className="mb-4 text-xl font-bold text-gray-900">ملخص الطلب</h3>
            <div className="space-y-3">
              {cartItems.map((item) => (
                <div key={item.id} className="flex items-center justify-between text-gray-700">
                  <span className="text-sm font-medium">
                    {item.name} (x{item.quantity})
                  </span>
                  <span>{(item.price * item.quantity).toFixed(2)} ر.س</span>
                </div>
              ))}
              <div className="my-4 h-px bg-gray-200"></div>
              <div className="flex justify-between text-gray-700">
                <span>المجموع الفرعي:</span>
                <span>{subtotal.toFixed(2)} ر.س</span>
              </div>
              <div className="flex justify-between text-gray-700">
                <span>الشحن:</span>
                <span className={shippingCost === 0 ? 'font-bold text-[#10B981]' : 'text-gray-700'}>
                  {shippingCost === 0 ? 'مجاني' : `${shippingCost.toFixed(2)} ر.س`}
                </span>
              </div>
              <div className="my-4 h-px bg-gray-200"></div>
              <div className="flex justify-between text-xl font-bold text-gray-900">
                <span>الإجمالي:</span>
                <span>{total.toFixed(2)} ر.س</span>
              </div>
            </div>
            <button
              onClick={handleConfirmOrder}
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-[#10B981] py-4 font-bold text-white shadow-sm shadow-[#10B981]/20 transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#0ea173] hover:shadow-lg hover:shadow-[#10B981]/25 active:translate-y-0"
            >
              تأكيد الطلب
              <ArrowRight className="h-5 w-5" />
            </button>
            <button
              onClick={() => setCurrentPage('cart')}
              className="mt-3 w-full rounded-xl border-2 border-gray-200 py-4 font-bold text-gray-700 transition-all duration-300 hover:-translate-y-0.5 hover:border-[#10B981] hover:text-[#10B981]"
            >
              العودة إلى السلة
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CheckoutPage;
